import { Plus } from "lucide-react";
import { useState } from "react";
import ProductModal from "@/components/ProductModal";

const ROZE_URL = "https://restoransroze.lv";
const eur = (v) => `€${Number(v).toFixed(2)}`;

const MenuItemCard = ({ item, language, translations, addToCart, setSelectedProduct, ordersDisabled }) => (
  <article
    className="rz-card group flex flex-col cursor-pointer focus-within:border-[#1C1917]"
    data-testid={`menu-item-${item.id}`}
    onClick={() => setSelectedProduct(item)}
  >
    <div className="relative aspect-[5/4] overflow-hidden bg-[#F5F2EC] rz-photo-warm rz-photo-warm--soft">
      <img
        src={item.image}
        alt={item[`name_${language}`]}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover"
        data-testid={`menu-item-image-${item.id}`}
      />
      {item.is_new && !item.original_price && (
        <span className="absolute top-4 left-4 bg-[#FDFBF7] text-[#1C1917] px-3 py-1.5 text-[0.62rem] font-medium tracking-[0.2em] uppercase" data-testid={`menu-item-new-${item.id}`}>
          {translations.newLabel[language]}
        </span>
      )}
      {item.original_price && (
        <span className="absolute top-4 left-4 bg-[#C2410C] text-white px-3 py-1.5 text-[0.62rem] font-medium tracking-[0.2em] uppercase" data-testid={`menu-item-discount-${item.id}`}>
          {item[`discount_label_${language}`] || 'SALE'}
        </span>
      )}
    </div>

    <div className="p-6 sm:p-7 flex flex-col flex-grow">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="font-display text-[1.4rem] sm:text-[1.55rem] leading-tight text-[#1C1917] m-0" data-testid={`menu-item-name-${item.id}`}>
          {item[`name_${language}`]}
        </h3>
        <div className="text-right shrink-0">
          {item.original_price && (
            <span className="block text-xs line-through text-[#A8A29E]">{eur(item.original_price)}</span>
          )}
          <span className="text-[1.05rem] font-semibold text-[#1C1917] tabular-nums" data-testid={`menu-item-price-${item.id}`}>
            {item.price == null ? <span className="text-[0.72rem] font-medium tracking-[0.14em] uppercase text-[#78716C]">{translations.priceTba[language]}</span> : eur(item.price)}
          </span>
        </div>
      </div>
      {item.category === 'pizza' && <span className="text-[0.62rem] tracking-[0.22em] uppercase text-[#78716C] mt-2">30 cm</span>}
      <p className="text-[0.95rem] leading-[1.7] text-[#57534E] mt-4 mb-0" data-testid={`menu-item-description-${item.id}`}>
        {item[`description_${language}`]}
      </p>
      {item.allergens && (
        <p className="text-[0.7rem] tracking-[0.08em] text-[#A8A29E] mt-3 mb-0">
          {translations.allergens[language]}: {item.allergens}
        </p>
      )}
      <div className="mt-auto pt-6 flex items-center justify-between gap-3">
        <span className="rz-link text-[0.66rem] font-medium tracking-[0.22em] uppercase text-[#78716C]">{translations.details[language]}</span>
        {ordersDisabled ? (
          <a
            href={ROZE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="rz-btn rz-btn--outline rz-btn--sm"
            data-testid={`roze-cta-${item.id}`}
          >
            {translations.tryAtRoze[language]}
          </a>
        ) : item.price == null ? (
          <span className="text-[0.66rem] font-medium tracking-[0.22em] uppercase text-[#A8A29E]" data-testid={`coming-soon-${item.id}`}>
            {translations.comingSoon[language]}
          </span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); addToCart(item); }}
            className="rz-btn rz-btn--outline rz-btn--sm"
            data-testid={`add-to-cart-${item.id}`}
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
            {translations.addToCart[language]}
          </button>
        )}
      </div>
    </div>
  </article>
);

const MenuCatalog = ({
  language,
  categories,
  menuItems,
  selectedCategory,
  setSelectedCategory,
  addToCart,
  loading,
  ordersDisabled = false
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const translations = {
    menu: { en: "The menu", ru: "Меню", lv: "Ēdienkarte" },
    sub: { en: "Choose for yourself or order for the whole group — delivery or pickup.", ru: "Выбери для себя или закажи на всю компанию — доставка или самовывоз.", lv: "Izvēlies sev vai pasūti visai kompānijai — piegāde vai līdzņemšana." },
    all: { en: "All", ru: "Все", lv: "Visi" },
    addToCart: { en: "Add", ru: "В корзину", lv: "Pievienot" },
    tryAtRoze: { en: "Try at Restaurant Roze", ru: "Отведать в ресторане Roze", lv: "Baudīt restorānā Roze" },
    loading: { en: "Loading menu...", ru: "Загрузка меню...", lv: "Ielādē ēdienkarti..." },
    allergens: { en: "Allergens", ru: "Аллергены", lv: "Alergēni" },
    newLabel: { en: "New", ru: "Новинка", lv: "Jaunums" },
    priceTba: { en: "Price TBA", ru: "Цена уточняется", lv: "Cena tiks precizēta" },
    comingSoon: { en: "Coming soon", ru: "Скоро в продаже", lv: "Drīzumā" },
    categories: { en: "Categories", ru: "Категории", lv: "Kategorijas" },
    details: { en: "Details", ru: "Подробнее", lv: "Vairāk" },
    items: { en: "items", ru: "позиций", lv: "pozīcijas" },
  };

  if (loading) {
    return (
      <div className="rz-page py-32 px-4 text-center" id="menu-section">
        <p className="rz-eyebrow">{translations.loading[language]}</p>
      </div>
    );
  }

  const grid = "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8";

  return (
    <section className="rz-page scroll-mt-[76px] pt-24 sm:pt-32 pb-24 sm:pb-32" id="menu-section" data-testid="menu-catalog-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 sm:mb-16">
          <div>
            <p className="rz-eyebrow m-0 mb-5">{translations.categories[language]}</p>
            <h2 className="font-display text-[clamp(2.4rem,5.5vw,4.2rem)] leading-[1] tracking-[-0.01em] m-0" data-testid="menu-title">
              {translations.menu[language]}
            </h2>
          </div>
          <p className="m-0 text-[#78716C] text-base max-w-[22rem] md:text-right">{translations.sub[language]}</p>
        </div>
      </div>

      <nav
        className="sticky top-[68px] sm:top-[76px] z-30 bg-[#FDFBF7] border-y border-[#E7E5E4]"
        aria-label={translations.categories[language]}
        data-testid="category-filters"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-3 flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button onClick={() => setSelectedCategory(null)} className="rz-pill" aria-pressed={selectedCategory === null} data-testid="category-all">
            {translations.all[language]}
          </button>
          {categories.map((category, index) => (
            <button
              key={`${category.id}-${index}`}
              onClick={() => setSelectedCategory(category.slug)}
              className="rz-pill"
              aria-pressed={selectedCategory === category.slug}
              data-testid={`category-${category.slug}`}
            >
              {category[`name_${language}`]}
            </button>
          ))}
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-12 sm:pt-16">
        {selectedCategory === null ? (
          <div data-testid="menu-items-grid">
            {categories.map((category) => {
              const categoryItems = menuItems.filter(item => item.category === category.slug);
              if (categoryItems.length === 0) return null;
              return (
                <div key={category.id} className="mb-20 sm:mb-28" data-testid={`menu-section-${category.slug}`}>
                  <div className="flex items-baseline justify-between gap-4 mb-8 sm:mb-10">
                    <h3 className="font-display text-[1.9rem] sm:text-[2.4rem] leading-none text-[#1C1917] m-0">
                      {category[`name_${language}`]}
                    </h3>
                    <span className="text-[0.68rem] tracking-[0.2em] uppercase text-[#A8A29E]">{categoryItems.length} {translations.items[language]}</span>
                  </div>
                  <div className={grid}>
                    {categoryItems.map((item) => (
                      <MenuItemCard key={item.id} item={item} language={language} translations={translations} addToCart={addToCart} setSelectedProduct={setSelectedProduct} ordersDisabled={ordersDisabled} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={grid} data-testid="menu-items-grid">
            {menuItems.map((item) => (
              <MenuItemCard key={item.id} item={item} language={language} translations={translations} addToCart={addToCart} setSelectedProduct={setSelectedProduct} ordersDisabled={ordersDisabled} />
            ))}
          </div>
        )}

        {selectedProduct && (
          <ProductModal
            item={selectedProduct}
            language={language}
            onClose={() => setSelectedProduct(null)}
            onAddToCart={addToCart}
            ordersDisabled={ordersDisabled}
          />
        )}
      </div>
    </section>
  );
};

export default MenuCatalog;
